// src/views/proprio/ProprioPhotoViewer.jsx — visionneuse plein écran (proprio).
//
// Affiche les photos d'un passage ménage (toutes pièces enchaînées) en plein
// écran sombre : navigation flèches (clavier ‹/›) + swipe mobile + bande de
// miniatures + label « pièce · photo N/total ». Lecture seule.
//
// Les URLs sont des signed URL (bucket privé, TTL 600s) — confortable pour la
// navigation. Sur erreur de chargement (TTL expiré après longue session),
// `onExpired` permet au parent de re-signer (re-fetch).

import { useEffect, useCallback, useRef } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { DS } from '../../lib/ui'

export default function ProprioPhotoViewer({ photos, index, onClose, onIndex, onExpired }) {
  const touchX = useRef(null)
  // Garde anti-boucle : on ne redemande une re-signature qu'UNE fois par URL
  // (une URL durablement morte ne re-déclenche pas onExpired à chaque render).
  const retried = useRef(new Set())
  const total = Array.isArray(photos) ? photos.length : 0
  const cur = total > 0 ? photos[index] : null

  const go = useCallback((delta) => {
    if (total > 0) onIndex(((index + delta) % total + total) % total)
  }, [index, total, onIndex])

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowLeft') go(-1)
      else if (e.key === 'ArrowRight') go(1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [go, onClose])

  if (!cur) return null

  const btn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,.14)', color: '#fff', border: 'none', borderRadius: 999, width: 44, height: 44, cursor: 'pointer', lineHeight: 1 }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.93)', display: 'flex', flexDirection: 'column', fontFamily: DS.fontBody }}>
      {/* Header : label + fermer */}
      <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', color: '#fff' }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>
          {cur.pieceName ? `${cur.pieceName} · ` : ''}photo {index + 1}/{total}
        </span>
        <button onClick={onClose} aria-label="Fermer" style={{ ...btn, width: 40, height: 40 }}><X size={20} strokeWidth={2} /></button>
      </div>

      {/* Image + flèches */}
      <div onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => { touchX.current = e.touches[0]?.clientX ?? null }}
        onTouchEnd={(e) => {
          const dx = (e.changedTouches[0]?.clientX ?? 0) - (touchX.current ?? 0)
          if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1)
        }}
        style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', padding: '0 8px' }}>
        {total > 1 && <button onClick={() => go(-1)} aria-label="Précédente" style={{ ...btn, position: 'absolute', left: 12 }}><ChevronLeft size={26} strokeWidth={2} /></button>}
        <img src={cur.signedUrl} alt={cur.fileName || ''}
          onError={() => { if (onExpired && cur.signedUrl && !retried.current.has(cur.signedUrl)) { retried.current.add(cur.signedUrl); onExpired() } }}
          style={{ maxWidth: '92%', maxHeight: '78vh', objectFit: 'contain', borderRadius: 6 }} />
        {total > 1 && <button onClick={() => go(1)} aria-label="Suivante" style={{ ...btn, position: 'absolute', right: 12 }}><ChevronRight size={26} strokeWidth={2} /></button>}
      </div>

      {/* Bande de miniatures */}
      {total > 1 && (
        <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '10px 12px', justifyContent: 'center' }}>
          {photos.map((p, i) => (
            <img key={i} src={p.signedUrl} alt="" onClick={() => onIndex(i)}
              style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 6, flexShrink: 0, cursor: 'pointer', border: i === index ? '2px solid #fff' : '2px solid transparent', opacity: i === index ? 1 : 0.55 }} />
          ))}
        </div>
      )}
    </div>
  )
}
